if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.filterBySelectedColor();

        this.variantChangeUnsubscriber =
          typeof subscribe === 'function' && window.PUB_SUB_EVENTS
            ? subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
                const productInfo = this.closest('product-info');
                if (!productInfo || data.sectionId !== productInfo.sectionId) return;

                window.requestAnimationFrame(() => {
                  this.filterBySelectedColor(data.variant);
                  this.setActiveMedia(data.variant?.featured_media?.id);
                });
              })
            : undefined;

        this.changeHandler = () => {
          window.requestAnimationFrame(() => this.filterBySelectedColor());
        };
        this.closest('product-info')?.addEventListener('change', this.changeHandler);
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.closest('product-info')?.removeEventListener('change', this.changeHandler);
      }

      setActiveMedia(mediaId) {
        if (!mediaId) {
          this.goToFirstVisibleSlide();
          return;
        }

        const mediaItem = this.querySelector(`[data-media-id="${mediaId}"]:not(.product-media-filter-hidden)`);
        const slide = mediaItem?.closest('.swiper-slide');
        const swiper = this.querySelector('.swiper-main')?.swiper;

        if (slide && swiper) {
          swiper.slideTo(Array.from(swiper.slides).indexOf(slide), 0);
        } else {
          this.goToFirstVisibleSlide();
        }
      }

      filterBySelectedColor(variant) {
        const selectedColor = this.getSelectedColor(variant);
        const colorTokens = this.getColorTokens();
        const selectedToken = this.normalizeColor(selectedColor);

        if (!selectedToken || colorTokens.length === 0) {
          this.toggleAllMedia(true);
          this.refreshSwipers();
          return;
        }

        const filterableItems = this.querySelectorAll(
          '.swiper-slide[data-media-id], .media-gallery__grid [data-media-id], .dialog-thumbnails-list__thumbnail[data-media-id], .dialog-zoomed-gallery [data-media-id]'
        );

        filterableItems.forEach((item) => {
          const mediaToken = this.getMediaColorToken(item, colorTokens);
          const shouldHide = mediaToken ? mediaToken !== selectedToken : true;

          item.classList.toggle('product-media-filter-hidden', shouldHide);
          item.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        });

        this.refreshSwipers();
        this.goToFirstVisibleSlide();
      }

      toggleAllMedia(show) {
        this.querySelectorAll('.product-media-filter-hidden').forEach((item) => {
          item.classList.toggle('product-media-filter-hidden', !show);
          item.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      }

      goToFirstVisibleSlide() {
        const swiper = this.querySelector('.swiper-main')?.swiper;
        if (!swiper) return;

        const firstVisibleSlide = Array.from(swiper.slides).find(
          (slide) => !slide.classList.contains('product-media-filter-hidden')
        );

        if (firstVisibleSlide) {
          swiper.slideTo(Array.from(swiper.slides).indexOf(firstVisibleSlide), 0);
        }
      }

      refreshSwipers() {
        this.querySelectorAll('.swiper').forEach((swiperElement) => {
          swiperElement.swiper?.update();
        });
      }

      getSelectedColor(variant) {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects[data-context^="main"]');
        const optionGroups = variantSelects ? Array.from(variantSelects.querySelectorAll('.product-form__input')) : [];
        const colorGroup = optionGroups.find((group) => this.isColorOption(group));
        const selectedInput = colorGroup?.querySelector('select option[selected], select option:checked, input:checked');

        return selectedInput?.value || variant?.option1 || variant?.title?.split(' / ')?.[0] || '';
      }

      getColorTokens() {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects[data-context^="main"]');
        const optionGroups = variantSelects ? Array.from(variantSelects.querySelectorAll('.product-form__input')) : [];
        const colorGroup = optionGroups.find((group) => this.isColorOption(group)) || optionGroups[0];

        if (!colorGroup) return [];

        return Array.from(colorGroup.querySelectorAll('select option, input[type="radio"]'))
          .map((option) => this.normalizeColor(option.value))
          .filter(Boolean)
          .sort((a, b) => b.length - a.length);
      }

      isColorOption(group) {
        const label = group.querySelector('legend, .form__label, label')?.textContent || group.textContent || '';
        return /color|colour|颜色|顏色/i.test(label);
      }

      getMediaColorToken(item, colorTokens) {
        const src = item.dataset.mediaSrc || item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
        const alt = item.dataset.mediaAlt || item.querySelector('img')?.alt || '';
        const fileName = this.getFileNameWithoutExtension(src);
        const fileColor = this.getColorFromFileName(fileName, colorTokens);

        if (fileColor) return fileColor;

        const altColor = this.getColorFromAltText(alt, colorTokens);
        if (altColor) return altColor;

        return '';
      }

      getFileNameWithoutExtension(value) {
        const cleanValue = String(value || '').split('?')[0].split('/').pop() || '';
        return cleanValue.replace(/\.(jpg|jpeg|png|webp|gif|avif)$/i, '').toLowerCase();
      }

      getColorFromFileName(fileName, colorTokens) {
        const parts = String(fileName || '').toLowerCase().split('-');
        if (parts.length < 3) return '';

        const colorPart = parts.slice(1, -1).join('');
        return colorTokens.find((token) => token === colorPart) || '';
      }

      getColorFromAltText(alt, colorTokens) {
        const colorText = String(alt || '').split(' - ').pop();
        const normalizedAltColor = this.normalizeColor(colorText);
        return colorTokens.find((token) => token === normalizedAltColor) || '';
      }

      normalizeColor(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9\u4e00-\u9fa5]/g, '');
      }
    }
  );
}
